import { Box, IconButton, Stack, Typography } from '@mui/joy';
import { Plus, TrashSimple } from '@phosphor-icons/react';
import { t } from 'i18next';
import { Control, useFieldArray } from 'react-hook-form';

import { companyPartFieldService } from 'categories/services';
import { ICompanyPartField } from 'categories/types';
import { Button, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { snakeToCamelLabel } from 'core/utils';
import { PartFieldOperator } from 'corporate/enums';
import { IPricingProfileCompanyPartFieldCriteriaForm } from 'pricing/components';
import { getOperatorOptions } from 'pricing/components/UpsertPricingProfileRule';
import { Color, Level, Size, Variant } from 'theme/enums';

interface IProps {
  name: string;
}

export const CompanyPartSpecificationField = ({ name }: IProps) => {
  const { control, errors } = useFormContext();

  const { fields, append, remove } = useFieldArray<{
    [key: string]: IPricingProfileCompanyPartFieldCriteriaForm[];
  }>({
    control: control as unknown as Control<{
      [key: string]: IPricingProfileCompanyPartFieldCriteriaForm[];
    }>,
    name,
  });

  const addCriteria = () => {
    append({
      companyPartField: null,
      operator: PartFieldOperator.EQ,
      value: '',
    } as IPricingProfileCompanyPartFieldCriteriaForm);
  };

  const removeCriteria = (index: number) => {
    remove(index);
  };

  return (
    <Stack gap={2}>
      <Typography level={Level.TITLE_MD}>{t('labelCompanyPartSpecificationCriteria')}</Typography>

      {fields.length > 0 && (
        <Stack gap={0}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '30% 20% 41% 9%',
              gap: 1,
            }}
          >
            <Typography level={Level.BODY_SM}>{t('labelPartField')}</Typography>
            <Typography level={Level.BODY_SM}>{t('labelOperation')}</Typography>
            <Typography level={Level.BODY_SM}>{t('labelValue')}</Typography>
            <Box />
          </Box>

          {fields.map((field, index) => {
            const fieldErrors = errors?.[name]?.[index];

            return (
              <Box
                key={field.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '30% 20% 41% 9%',
                  gap: 1,
                }}
              >
                <FormField
                  name={`${name}.${index}.companyPartField`}
                  type={InputType.AUTOCOMPLETE}
                  inputProps={{
                    getOptions: companyPartFieldService.getCompanyPartFields,
                    getLabel: (option: ICompanyPartField) => t(snakeToCamelLabel(option.name)),
                    required: true,
                    control,
                    error: fieldErrors?.companyPartField,
                  }}
                />
                <FormField
                  name={`${name}.${index}.operator`}
                  type={InputType.SELECT}
                  inputProps={{
                    options: getOperatorOptions(),
                    required: true,
                    control,
                    error: fieldErrors?.operator,
                  }}
                />
                <FormField
                  name={`${name}.${index}.value`}
                  type={InputType.TEXT_INPUT}
                  inputProps={{
                    required: true,
                    control,
                    error: fieldErrors?.value,
                  }}
                />
                <Box>
                  <IconButton
                    color={Color.DANGER}
                    size={Size.SM}
                    onClick={() => removeCriteria(index)}
                  >
                    <TrashSimple />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      <Button
        text={t('buttonLabelAddCompanyPartSpecificationCriteria')}
        variant={Variant.PLAIN}
        onClick={addCriteria}
        startDecorator={<Plus size={16} />}
        sx={{ width: 'fit-content' }}
      />
    </Stack>
  );
};
